import { useReducer } from 'react';
import { HamletClient } from '../service/check-email-page/mini-hamlet-client';

import {
  CheckEmailAnalyticsInterface,
  ResendVerificationEmailRequestType
} from '../model/check-email-page';

export enum ResendEmailStatuses {
  'NO_STATUS',
  'REQUESTING',
  'SUCCESS',
  'FAILURE'
}

enum ResendEmailActions {
  'SEND_REQUEST',
  'SUCCESS',
  'FAILURE'
}

export type ResendEmailState = {
  status: ResendEmailStatuses;
  error?: string;
};

type ResendEmailAction = {
  type: ResendEmailActions;
  error?: string;
};

const initialState: ResendEmailState = {
  status: ResendEmailStatuses.NO_STATUS,
  error: undefined
};

/**
 * useReducer requires these parameters, but TS won't allow us to have 'state' here unused.
 * Instead, we can use _state to indicate to TS that we're alright with not using this variable, but then
 * TSLint complains about our variable name not matching the required format, so we're disabling that rule here.
 */
/* tslint:disable-next-line:variable-name */
const reducer = (_state: ResendEmailState, action: ResendEmailAction) => {
  switch (action.type) {
    case ResendEmailActions.SEND_REQUEST:
      return {
        ...initialState,
        status: ResendEmailStatuses.REQUESTING
      };
    case ResendEmailActions.SUCCESS:
      return {
        ...initialState,
        status: ResendEmailStatuses.SUCCESS
      };
    case ResendEmailActions.FAILURE:
      return {
        ...initialState,
        status: ResendEmailStatuses.FAILURE,
        error: action.error
      };
  }
};

interface UseResendEmailAPIProps {
  baseUrl?: string;
  checkEmailAnalyticsService: CheckEmailAnalyticsInterface;
}

const useResendEmailAPI = ({
  baseUrl,
  checkEmailAnalyticsService
}: UseResendEmailAPIProps) => {
  const client = new HamletClient({ baseUrl });
  const [state, dispatch] = useReducer(reducer, initialState);

  const triggerResendEmail = (
    resendEmailData: ResendVerificationEmailRequestType
  ) => {
    dispatch({ type: ResendEmailActions.SEND_REQUEST });

    return client.sendEmail(resendEmailData).then((data) => {
      if (data?.status === 200) {
        checkEmailAnalyticsService.resendApiSuccess();
      }
      if (data?.status === 204) {
        checkEmailAnalyticsService.resendApiNoContent();
      }
      if (data?.status === 304) {
        checkEmailAnalyticsService.resendApiAlreadyVerified();
      }

      if (data.success) {
        dispatch({ type: ResendEmailActions.SUCCESS });
        return true;
      }

      checkEmailAnalyticsService.resendApiFailure();
      dispatch({ type: ResendEmailActions.FAILURE });
      return false;
    });
  };

  return { state, triggerResendEmail };
};

export default useResendEmailAPI;
